import React, { useEffect, useLayoutEffect } from "react";

const useIsomorphicLayoutEffect =
  typeof document === "undefined" ? useEffect : useLayoutEffect;

export const BUILDER_STYLE_TAG_ID = "cf-builder-consolidated-styles";
export const BUILDER_STYLE_BUCKETS = ["theme", "steps", "sections", "elements"];
export const BUILDER_STYLE_BUCKET_DEFAULT = "elements";

const registries = new Map(BUILDER_STYLE_BUCKETS.map((b) => [b, new Map()]));
const syncTimers = new Map();
let bucketTagsInitialized = false;

const getBucketStyleElement = (bucket) => {
  const id = `cf-builder-styles-${bucket}`;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    el.setAttribute("data-cf-builder-styles", bucket);
    document.head.appendChild(el);
  }
  return el;
};

const ensureBucketTagsCreated = () => {
  if (bucketTagsInitialized || typeof document === "undefined") return;
  BUILDER_STYLE_BUCKETS.forEach(getBucketStyleElement);
  bucketTagsInitialized = true;
};

const syncBucket = (bucket) => {
  clearTimeout(syncTimers.get(bucket));
  syncTimers.set(
    bucket,
    setTimeout(() => {
      syncTimers.delete(bucket);
      const registry = registries.get(bucket);
      if (!registry) return;

      ensureBucketTagsCreated();
      const styleElement = getBucketStyleElement(bucket);

      if (registry.size === 0) {
        styleElement.remove();
        return;
      }

      const next = Array.from(registry.keys())
        .sort()
        .map((k) => registry.get(k))
        .filter(Boolean)
        .join("\n");

      if (styleElement.textContent === next) return;
      styleElement.textContent = next;
    }, 16)
  );
};

export const registerBuilderStyles = (
  styleKey,
  stylesheet,
  bucket = BUILDER_STYLE_BUCKET_DEFAULT
) => {
  if (!styleKey || !stylesheet) return;
  registries.get(bucket).set(styleKey, stylesheet);
  syncBucket(bucket);
};

export const unregisterBuilderStyles = (
  styleKey,
  bucket = BUILDER_STYLE_BUCKET_DEFAULT
) => {
  if (!styleKey) return;
  registries.get(bucket).delete(styleKey);
  syncBucket(bucket);
};

export const clearBuilderStyleRegistry = () => {
  BUILDER_STYLE_BUCKETS.forEach((bucket) => {
    registries.get(bucket).clear();
    syncBucket(bucket);
  });
};

// Removes rules with no declarations (`.selector { }`) — resolvers emit heavily
// conditional declarations, so default-valued settings leave behind empty rules
// whose selectors are the expensive part. Loops so @media shells emptied by a
// pass collapse on the next one. `;` is excluded from the selector match so
// statements like `@import url(...);` are never swallowed.
export const stripEmptyCssRules = (stylesheet) => {
  if (!stylesheet) return stylesheet;

  let css = stylesheet;
  let previous;
  do {
    previous = css;
    css = css.replace(/[^{};]*\{\s*\}/g, "");
  } while (css !== previous);
  return css.trim();
};

const BuilderStyleTag = ({
  builder,
  styleKey,
  stylesheet,
  bucket = BUILDER_STYLE_BUCKET_DEFAULT,
}) => {
  const cleanedStylesheet = React.useMemo(
    () => stripEmptyCssRules(stylesheet),
    [stylesheet]
  );

  const shouldConsolidate =
    builder === true &&
    styleKey &&
    cleanedStylesheet &&
    typeof document !== "undefined";

  useIsomorphicLayoutEffect(() => {
    if (!shouldConsolidate) return undefined;
    registerBuilderStyles(styleKey, cleanedStylesheet, bucket);
    return () => unregisterBuilderStyles(styleKey, bucket);
  }, [shouldConsolidate, styleKey, cleanedStylesheet, bucket]);

  if (shouldConsolidate || !cleanedStylesheet) return null;

  return <style dangerouslySetInnerHTML={{ __html: cleanedStylesheet }} />;
};

export default BuilderStyleTag;
